import { useCallback, useMemo, useRef, useState } from "react";
import type { VizRenderPayload } from "@/lib/viz/types";
import { captureVizImage } from "@/lib/viz/captureVizImage";
import { VizRenderer } from "./VizRenderer";
import { VizChrome } from "./VizChrome";
import { VizTabStrip } from "./VizTabStrip";
import { RefineChartInput } from "./RefineChartInput";
import { AskAboutDataInput } from "./AskAboutDataInput";
import { RerunWithFiltersButton } from "./RerunWithFiltersButton";
import { SaveVizButton } from "./SaveVizButton";
import { SuggestedQuestions } from "./SuggestedQuestions";

interface Props {
  vizSpec: VizRenderPayload;
  sourceRef?: { kind: string; params: Record<string, unknown> } | null;
  /** True while any of the three follow-up actions is in flight. */
  busy: boolean;
  /** Refine the chart against its cached rows (Feature #1). */
  onFollowUp?: (intent: string, columnNames: string[], rows: Array<Array<unknown>>) => void;
  /** Ask a narrative question about the data (Feature #3). */
  onAskAboutData?: (intent: string, columnNames: string[], rows: Array<Array<unknown>>) => void;
  /** Re-run the Cognos report with new prompt-parameter values (Feature #2). */
  onRerunWithFilters?: (
    sourceRef: { kind: string; params: Record<string, unknown> },
    values:    Record<string, string | string[]>,
  ) => void;
}

/**
 * Renders a viz-pipeline chart in the operate-ui chat thread, plus
 * the three follow-up affordances:
 *   - "Refine this chart" — re-spec cached rows (Feature #1)
 *   - "Re-run with filters" — re-run with new prompts (Feature #2)
 *   - "Ask about this data" — narrative answer (Feature #3)
 *
 * Each affordance is gated on whether its prerequisite is met
 * (callback supplied + cached rows present + sourceRef present for
 * filters). When none apply the chart renders bare — same shape, no
 * trailing inputs.
 */
export function VisualBlock({
  vizSpec, sourceRef, busy, onFollowUp, onAskAboutData, onRerunWithFilters,
}: Props) {
  const hasRows = vizSpec.rows.length > 0;
  const showFilters = !!(sourceRef?.kind === "cognos.report.run" && onRerunWithFilters);

  // Tab strip state. activeIndex=0 means render the primary spec exactly
  // as the backend shipped it; 1..N means render candidates[index - 1].
  // Resetting to 0 whenever the upstream vizSpec changes (i.e. a fresh
  // turn arrived) is necessary because the candidates list is a property
  // of *this* turn — keeping a stale index from a previous chart would
  // crash when the new candidates array is shorter.
  const candidates = vizSpec.candidates ?? [];
  const [activeIndex, setActiveIndex] = useState(0);
  const renderPayload = useMemo<VizRenderPayload>(() => {
    if (activeIndex === 0 || activeIndex - 1 >= candidates.length) return vizSpec;
    const c = candidates[activeIndex - 1];
    // Build a derived payload that swaps the spec triplet but keeps the
    // same rows/columnNames (Vega-Lite reads data from the embedded
    // values; the renderer also uses rows/columnNames as a tabular
    // fallback when the spec fails to render).
    return {
      ...vizSpec,
      format:   c.format,
      vizType:  c.vizType,
      specJson: c.specJson,
    };
  }, [vizSpec, candidates, activeIndex]);

  // Reset to primary whenever a new top-level vizSpec arrives. Comparing
  // by specJson identity is enough — every new turn ships a fresh string.
  const lastSpecJsonRef = useRef(vizSpec.specJson);
  if (lastSpecJsonRef.current !== vizSpec.specJson) {
    lastSpecJsonRef.current = vizSpec.specJson;
    if (activeIndex !== 0) setActiveIndex(0);
  }

  // Ref on the rendered-viz wrapper so "Save as Cognos report" can
  // capture the chart the user sees and embed it in the saved report.
  // captureImage is lazy — invoked at save time, not on every render —
  // and returns null when nothing capturable is present (the save then
  // falls back to a table-only report).
  const vizRef = useRef<HTMLDivElement>(null);
  const captureImage = useCallback(() => captureVizImage(vizRef.current), []);

  return (
    <div className="space-y-2">
      {/* VizChrome handles the bubble border, title header, and the
          toolbar (zoom / fullscreen). The follow-up affordances
          (refine / ask / suggest) stay outside the chrome so they
          remain visible when the chart is zoomed and so fullscreen
          doesn't accidentally include the input controls.

          Tab strip sits ABOVE the chrome (not inside) so it doesn't
          inherit the chart's scale transform — users zooming the
          chart shouldn't blow up the tab icons too. Only renders when
          the backend shipped at least one alternate: a one-tab strip
          is noise. Same data (rows / columnNames) underlies every
          tab; only the spec changes. */}
      <div ref={vizRef}>
        {candidates.length > 0 && (
          <VizTabStrip
            primary={{
              vizType:  vizSpec.vizType,
              label:    "Default view",
              iconHint: vizSpec.vizType,
            }}
            candidates={candidates}
            activeIndex={activeIndex}
            onSelect={setActiveIndex}
          />
        )}
        <VizChrome columnNames={vizSpec.columnNames} rows={vizSpec.rows}>
          <VizRenderer payload={renderPayload} />
        </VizChrome>
      </div>

      {hasRows && onFollowUp && (
        <RefineChartInput
          columnNames={vizSpec.columnNames}
          rows={vizSpec.rows}
          busy={busy}
          onSubmit={onFollowUp}
          extraAction={
            <>
              {showFilters && sourceRef && (
                <RerunWithFiltersButton
                  sourceRef={sourceRef}
                  onRerun={onRerunWithFilters!}
                  disabled={busy}
                />
              )}
              <SaveVizButton
                columnNames={vizSpec.columnNames}
                rows={vizSpec.rows}
                captureImage={captureImage}
                disabled={busy}
                sourceRef={vizSpec.sourceRef ?? sourceRef ?? null}
                vizSpec={{
                  format:   renderPayload.format,
                  vizType:  renderPayload.vizType,
                  specJson: renderPayload.specJson,
                }}
              />
            </>
          }
        />
      )}

      {hasRows && onAskAboutData && (
        <AskAboutDataInput
          columnNames={vizSpec.columnNames}
          rows={vizSpec.rows}
          busy={busy}
          onSubmit={onAskAboutData}
        />
      )}

      {hasRows && onAskAboutData && (
        <SuggestedQuestions
          sourceRef={sourceRef ?? null}
          renderedColumns={vizSpec.columnNames}
          busy={busy}
          onPick={(question) => onAskAboutData(question, vizSpec.columnNames, vizSpec.rows)}
        />
      )}
    </div>
  );
}
